/**
 * PriorContractFormDialog Component
 * Modal for manually creating a historical (pre-go-live) contract stub.
 * Used when no prior contract exists in the system to link as a rolled-from source.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { apiPost, apiGet } from '../../utils/apiClient';

const PriorContractFormDialog = ({ open, onClose, onSaved, investorId, investorName }) => {
  const [formData, setFormData] = useState({
    user_reference: '',
    product_id: '',
    deposit_date: '',
    maturity_date: '',
    investment_currency_id: '',
    capital_amount: '',
  });
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load reference data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        user_reference: '',
        product_id: '',
        deposit_date: '',
        maturity_date: '',
        investment_currency_id: '',
        capital_amount: '',
      });
      setError(null);

      if (products.length === 0) {
        apiGet('/refdata/investment-types?active_only=true').then(res => {
          if (res?.success) setProducts(res.data || []);
        }).catch(() => {});
      }
      if (currencies.length === 0) {
        apiGet('/refdata/countries?active_only=true').then(res => {
          if (res?.success) setCurrencies(res.data || []);
        }).catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currencies.length and products.length are intentional one-time load guards
  }, [open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isValid = formData.user_reference
    && formData.product_id
    && formData.deposit_date
    && formData.maturity_date
    && formData.investment_currency_id
    && parseFloat(formData.capital_amount) > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost('/contracts/historical', {
        user_reference: formData.user_reference,
        investor_id: investorId,
        product_id: parseInt(formData.product_id),
        deposit_date: formData.deposit_date,
        maturity_date: formData.maturity_date,
        investment_currency_id: parseInt(formData.investment_currency_id),
        capital_amount: parseFloat(formData.capital_amount),
      });
      if (response?.success && response.data) {
        onSaved(response.data);
        onClose();
      } else {
        setError(response?.message || 'Failed to create historical contract');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create historical contract');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">Enter Historical Contract</Typography>
        <Typography variant="body2" color="textSecondary">
          Capture details of a prior contract that pre-dates the system.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={12}>
            <TextField
              label="External Reference *"
              fullWidth
              value={formData.user_reference}
              onChange={(e) => handleChange('user_reference', e.target.value)}
              helperText="The reference used for this contract in the prior system"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Investor"
              fullWidth
              value={investorName || ''}
              InputProps={{ readOnly: true }}
              variant="filled"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="Product Type *"
              select
              fullWidth
              value={formData.product_id}
              onChange={(e) => handleChange('product_id', e.target.value)}
              InputLabelProps={{ shrink: true }}
            >
              {products.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.type}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="Currency *"
              select
              fullWidth
              value={formData.investment_currency_id}
              onChange={(e) => handleChange('investment_currency_id', e.target.value)}
              InputLabelProps={{ shrink: true }}
            >
              {currencies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.currency_code} - {c.country}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="Start Date *"
              type="date"
              fullWidth
              value={formData.deposit_date}
              onChange={(e) => handleChange('deposit_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="End Date *"
              type="date"
              fullWidth
              value={formData.maturity_date}
              onChange={(e) => handleChange('maturity_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Capital Amount *"
              type="number"
              fullWidth
              value={formData.capital_amount}
              onChange={(e) => handleChange('capital_amount', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          Create Historical Contract
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriorContractFormDialog;
